import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { DateTime, Interval } from 'luxon';
import { distinctUntilChanged, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { EntityType, GetReferral, GetReferralDetails, ReferralStatus, UpsertNotification } from '../api-module/control-centre-api-client';
import { of, timer, combineLatest, Subject, Observable, Subscription, BehaviorSubject } from 'rxjs';
import { OrgSiteSelectionService } from '../shared/org-site-selection/org-site-selection.service';
import { ReferralsService } from './referrals.service';
import { ReferralsUtility } from './referrals.utility';
import equal from 'fast-deep-equal';
import { v4 as uuidv4 } from 'uuid';
import { Store } from '@ngxs/store';
import * as ReferralFiltersActions from './referral-filters/referral-filters.actions';
import { ReferralFiltersState } from './referral-filters/referral-filters.state';

@Component({
    selector: 'app-referrals',
    templateUrl: './referrals.component.html',
    styleUrls: ['./referrals.component.scss']
})
export class ReferralsComponent implements OnInit, OnDestroy {
    public static readonly INTERVAL_RRB_PERIODIC_REFRESH = 1000 * 60 * 10; // 10 minutes

    // Tracks when the component is destroyed, to handle unsubscribing to timers
    private readonly destroy$ = new Subject();
    rowActioned$ = new BehaviorSubject(null);
    private hubSubscriptionId: string;

    loading = false;
    referralDataSource: (GetReferral | GetReferralDetails)[] = [];

    private readonly subscriptions = new Subscription();

    clearSearch$ = new EventEmitter<void>();

    filtersVisibleSource = new BehaviorSubject<boolean>(false);
    filtersApplied$: Observable<boolean> = this.store.select(ReferralFiltersState.filtersApplied);

    filteredKeywordsSource = new BehaviorSubject<string>('');

    filterTimeRange$ = this.store.select(ReferralFiltersState.getTimeRangeFilter);
    filteredModalityTypes$ = this.store.select(ReferralFiltersState.getFilteredModalities);
    filteredUrgencyTypes$ = this.store.select(ReferralFiltersState.getFilteredUrgencies);
    filteredStatusTypes$ = this.store.select(ReferralFiltersState.getFilteredStatuses);

    upsertedReferrals = new BehaviorSubject<Map<number, GetReferral | GetReferralDetails>>(
        new Map<number, GetReferral | GetReferralDetails>()
    );
    deletedReferrals = new BehaviorSubject<Set<number>>(new Set<number>());

    triggerWarningIconCheck = new Subject();

    hasReferralsAwaitingAcknowledgement = false;

    constructor(
        private readonly referralsService: ReferralsService,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        private readonly store: Store
    ) {}

    ngOnInit(): void {
        this.initReferralList();
    }

    async ngOnDestroy(): Promise<void> {
        await this.removeHubSubscription();
        this.subscriptions.unsubscribe();
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    private initReferralList() {
        const orgSelected$ = this.orgSiteSelectionService.getOrgSelected$();
        const hasOnlyOneSite$ = this.orgSiteSelectionService.hasOnlyOneSite();
        const sitesSelected$ = this.orgSiteSelectionService.getSitesSelected();

        const userSite$ = combineLatest([orgSelected$, sitesSelected$, hasOnlyOneSite$]).pipe(takeUntil(this.destroy$));

        this.subscriptions.add(
            userSite$.subscribe(async ([org, siteIds, _]) => {
                if (org) {
                    await this.initHubSubscription(org.orgCode, siteIds);
                }
            })
        );

        const referralsSource$ = userSite$.pipe(
            switchMap(([org, siteIds, hasOnlyOneSite]): Observable<(GetReferral | GetReferralDetails)[]> => {
                if (!org) {
                    return of([]);
                }

                const { orgWide } = org.getAllowedSites();

                // if user has org wide permission, and user is not selecting a single site, show referrals from the org
                if (orgWide && (siteIds.length !== 1 || hasOnlyOneSite || siteIds[0] === null)) {
                    siteIds = [];
                }

                const filtersAndTimerAndTrigger$ = combineLatest([
                    timer(0, ReferralsComponent.INTERVAL_RRB_PERIODIC_REFRESH).pipe(takeUntil(this.destroy$)),
                    this.filteredModalityTypes$.pipe(
                        map((modalityTypes: string[]) =>
                            modalityTypes.map(modality => (modality === 'PlainFilm' ? ['CR', 'DX'] : modality)).flat()
                        )
                    ),
                    this.filteredUrgencyTypes$,
                    this.filteredStatusTypes$,
                    this.filterTimeRange$,
                    this.rowActioned$
                ]);

                return filtersAndTimerAndTrigger$.pipe(
                    switchMap(([_timer, filteredModalityTypes, filteredUrgencyTypes, filteredStatusTypes, filterTimeRange, _trigger]) => {
                        this.loading = true;
                        this.clearChangesOnRefresh();

                        const filteredStatuses = [...filteredStatusTypes];
                        if (filteredStatusTypes.includes(ReferralStatus.AwaitingAcknowledgement)) {
                            filteredStatusTypes.push(ReferralStatus.FollowedUp);
                            filteredStatusTypes.push(ReferralStatus.RequiresFollowUp);
                            filteredStatusTypes.push(ReferralStatus.FollowUpInProgress);
                        }

                        return this.referralsService
                            .getReferralList({
                                orgCode: org.orgCode,
                                siteIds,
                                createdAtFrom: filterTimeRange.start.toISO(),
                                createdAtTo: filterTimeRange.end.toISO(),
                                urgency: filteredUrgencyTypes,
                                status: filteredStatuses,
                                modality: filteredModalityTypes
                            })
                            .pipe(
                                tap(() => (this.loading = false)),
                                switchMap(referrals => this.removeDeletedReferrals(referrals)),
                                switchMap(referrals => this.upsertChangedReferrals(referrals)),
                                tap(() => this.checkReferralsAwaitingAcknowledgement()),
                                switchMap(referrals => this.filterReferralsByTimeRange(referrals)),
                                map(rows => rows.map(ReferralsUtility.transformToStatusAwaitingAcknowledgement))
                            );
                    })
                );
            }),
            shareReplay()
        );

        const filteredReferralsSource$ = combineLatest([referralsSource$, this.filteredKeywordsSource]).pipe(
            map(([referrals, searchTerm]) => this.filterReferralsByKeywords(referrals, searchTerm)),
            shareReplay(),
            distinctUntilChanged((prev, current) => equal(prev, current))
        );

        this.subscriptions.add(
            filteredReferralsSource$.subscribe(val => {
                this.referralDataSource = val ?? [];
            })
        );

        this.subscriptions.add(
            this.referralsService.hasReferralsAwaitingAcknowledgement$.subscribe(hasUnacknowledgedReferrals => {
                this.hasReferralsAwaitingAcknowledgement = hasUnacknowledgedReferrals;
            })
        );
    }

    async initHubSubscription(orgCode: string, siteIds?: number[]): Promise<void> {
        try {
            if (this.hubSubscriptionId) {
                await this.removeHubSubscription();
            }
            this.hubSubscriptionId = uuidv4();
            await this.referralsService.subscribeToReferralUpdates(
                this.hubSubscriptionId,
                orgCode,
                siteIds,
                upsertNotification => this.onEntityUpsert(upsertNotification),
                (entityId, entityType) => this.onEntityDelete(entityId, entityType)
            );
        } catch (error) {
            console.warn(error);
        }
    }

    async removeHubSubscription(): Promise<void> {
        await this.referralsService.unsubscribeFromReferralUpdates(this.hubSubscriptionId);
    }

    private checkReferralsAwaitingAcknowledgement() {
        this.referralsService.checkReferralsAwaitingAcknowledgement$.next(null);
    }

    clearChangesOnRefresh(): void {
        this.upsertedReferrals.next(new Map<number, GetReferral | GetReferralDetails>());
        this.deletedReferrals.next(new Set<number>());
    }

    filterReferralsByKeywords(referrals: (GetReferral | GetReferralDetails)[], searchTerm: string): (GetReferral | GetReferralDetails)[] {
        if (referrals.length > 0 && searchTerm) {
            const searchFunction = this.getReferralMatchingFunction(searchTerm);
            return referrals.filter(searchFunction);
        }
        return referrals;
    }

    getReferralMatchingFunction(searchTerm: string): (getReferral) => boolean {
        searchTerm = searchTerm.toLowerCase();
        return (referral: GetReferral) => {
            const patient = referral.patient;
            const patientName = ReferralsUtility.DisplayPersonName(referral.patient);
            const urgencyText = ReferralsUtility.GetUrgencyText(referral);
            const statusText = ReferralsUtility.GetStatusText(referral.status);
            return (
                patientName.toLowerCase().includes(searchTerm) ||
                patient.patientExternalId.toLowerCase().includes(searchTerm) ||
                referral.referrerName.toLowerCase().includes(searchTerm) ||
                urgencyText.toLowerCase().includes(searchTerm) ||
                statusText.toLowerCase().includes(searchTerm)
            );
        };
    }

    filterTimeRangeChange(interval: Interval) {
        this.store.dispatch(new ReferralFiltersActions.UpdateTimeRangeFilter(interval));
    }

    openFilters() {
        this.filtersVisibleSource.next(true);
    }

    closeFilters() {
        this.filtersVisibleSource.next(false);
    }

    clearFilters() {
        this.store.dispatch(new ReferralFiltersActions.ClearFilters());
    }

    onVisualRowSizeChanged(visualRowSize: number) {
        this.referralsService.referralsCount$.next(visualRowSize);
    }

    onRowActioned() {
        this.rowActioned$.next(null);
    }

    onUpdatingKeywords(keyword: string) {
        this.filteredKeywordsSource.next(keyword);
    }

    filterReferralsByTimeRange(items: (GetReferral | GetReferralDetails)[]): Observable<(GetReferral | GetReferralDetails)[]> {
        return this.store
            .select(ReferralFiltersState.getTimeRangeFilter)
            .pipe(
                map(
                    filter =>
                        items?.filter(
                            item =>
                                filter.start <= DateTime.fromISO(item.createdAt || DateTime.now().toISO()) &&
                                filter.end >= DateTime.fromISO(item.createdAt || DateTime.now().toISO())
                        ) ?? []
                )
            );
    }

    onEntityUpsert(upsertNotification: UpsertNotification) {
        if (upsertNotification.baseEntityType === EntityType.Referral) {
            const upsertedReferral = upsertNotification.updatedEntityDetails as GetReferralDetails;
            const upsertedReferralsMap = this.upsertedReferrals.value;
            let changed = false;
            const storedReferral = upsertedReferralsMap.get(upsertedReferral.id);
            if (storedReferral) {
                // Only update if it is not an older version, otherwise don't bother.
                if (storedReferral.updatedAt <= upsertedReferral.updatedAt) {
                    upsertedReferralsMap.set(upsertedReferral.id, upsertedReferral);
                    changed = true;
                }
            } else {
                upsertedReferralsMap.set(upsertedReferral.id, upsertedReferral);
                changed = true;
            }
            if (changed) {
                this.upsertedReferrals.next(upsertedReferralsMap);
            }
        }
    }

    onEntityDelete(entityId: string, entityType: EntityType) {
        if (entityType === EntityType.Referral) {
            const referralId = Number(entityId);
            if (referralId) {
                const deletedReferrals = this.deletedReferrals.value;
                if (!deletedReferrals.has(referralId)) {
                    deletedReferrals.add(referralId);
                    this.deletedReferrals.next(deletedReferrals);
                }

                // Remove from upserts too, as it may have been updated then deleted
                const upsertedReferrals = this.upsertedReferrals.value;
                if (upsertedReferrals.has(referralId)) {
                    upsertedReferrals.delete(referralId);
                    this.upsertedReferrals.next(upsertedReferrals);
                }
            }
        }
    }

    upsertChangedReferrals(referrals: GetReferral[]): Observable<(GetReferral | GetReferralDetails)[]> {
        return this.upsertedReferrals.pipe(
            map(upsertedReferrals => {
                const updatedReferrals: (GetReferral | GetReferralDetails)[] = [...referrals];
                upsertedReferrals.forEach(upsertedReferral => {
                    const indexOfMatchingReferral = updatedReferrals.findIndex(referral => referral.id === upsertedReferral.id);
                    if (indexOfMatchingReferral >= 0) {
                        updatedReferrals.splice(indexOfMatchingReferral, 1);
                    }
                    updatedReferrals.push(upsertedReferral);
                });
                return updatedReferrals;
            })
        );
    }

    removeDeletedReferrals(referrals: GetReferral[]): Observable<GetReferral[]> {
        if (referrals.length > 0) {
            return this.deletedReferrals.pipe(map(deletedReferrals => referrals.filter(referral => !deletedReferrals.has(referral.id))));
        }

        return of([]);
    }
}
